import { ofType } from 'redux-observable';
import { Observable, EMPTY, of, merge, BehaviorSubject, Subject, from } from 'rxjs';
import { take, tap, mergeMap, catchError, switchMap, takeWhile, takeUntil } from 'rxjs/operators';

import { ModalService } from 'ui-components/services/modal-service';

import * as Actions from './bulk.purchase.actions';
import { CostReviewModalContainer } from '../../../containers/cost-review-modal';
import { FeatureFlags } from '../../../feature-flags/feature-flags';
import { LabelBatchStatusStep } from '../../../enums/label-batch-status-step';
import { createLabelSubject } from '../../../fulfillment-planner/context';
import { getSummary } from '../../../fulfillment-planner/rate-recommendation/rate-recommendations.api';
import { BulkPurchaseNotificationService } from '../../../services/bulk-purchase-noto-service/';
import { InsuranceTermsService, hasUserAcceptedInsuranceTerms } from '../../../services/insurance-terms-service';
import { LogService } from '../../../services/log-service';
import Global from '../../../store/global-selectors';
import * as PostagePurchaseActions from '../../../store/postage-purchase/postage-purchase.actions';
import { startCostReviewGetMessages } from '../../cost-review';
import { determineShouldShowCostReviewByCostReviewBalances } from '../../quickship/quickship.helpers';
import { bulkApplyRecommendationsFromLabelPurchaseCostReview } from '../../recommendations/recommendations.observables';
import { HAS_ACCEPTED_UPS_LABEL_TERMS_SETTING } from '../../../wallet/utilities/constants';
import { saveNewSellerSettings } from '../../seller/seller.actions';
import { COST_REVIEW_TIMEOUT_ERROR } from '../bulk-error-types';
import * as BulkActions from '../bulk.actions';
import * as BulkApi from '../bulk.api';
import { getIsBulkRateShopperRecommendationsEnabled } from '../../../hooks/use-is-bulk-rate-shopper-recommendations-enabled';

let costReviewModalId;
const openCostReviewModal = ({ fulfillmentPlanId, jobGuid, costReviewData, observer }) => {
  costReviewModalId = ModalService.launchModal({
    component: CostReviewModalContainer,
    onClose: () => {
      observer.next(Actions.cancelCostReview(jobGuid));
      observer.complete();
    },
    onCancel: () => {
      createLabelSubject.next({ fulfillmentPlanId, isCreatingLabel: false });
      observer.next(Actions.cancelCostReview(jobGuid));
      observer.complete();
    },
    props: {
      jobGuid,
      costReviewData,
    },
  });
};
const onCloseCostReviewModal = ({ action$ }) => {
  return action$.pipe(
    ofType(
      PostagePurchaseActions.POSTAGE_AND_INSURANCE_PURCHASE_SUCCESS,
      // cancelled
      Actions.BULK_PURCHASE_END,
    ),
    take(1),
    tap(() => ModalService.closeModalById(costReviewModalId)),
    mergeMap(() => EMPTY),
  );
};

// exported for testing
export const launchCostReviewModal = ({ jobGuid, costReviewData = {}, shouldShowLegacyCostReview, getState, included }) => {
  const state = getState();
  const orderHasInsurance = (costReviewData?.insuranceCosts || []).length !== 0;
  // NOTE: At time of writing, insuranceCosts will only contain no more than one insurance provider.
  // This will need to be updated whenever a seller can combine multiple insurance providers in the same purchase
  const insuranceProviderId = costReviewData?.insuranceCosts?.[0]?.insuranceProviderId;
  const updateLabelBatchStatusStepAction = BulkActions.bulkSetActiveLabelBatchStatusStep({
    step: LabelBatchStatusStep.CostReview.ordinal,
  });

  if (!orderHasInsurance || hasUserAcceptedInsuranceTerms(state, insuranceProviderId)) {
    return new Observable((observer) => {
      if (shouldShowLegacyCostReview) {
        openCostReviewModal({
          fulfillmentPlanId: included[0],
          jobGuid,
          costReviewData,
          observer,
        });
      } else {
        observer.next(updateLabelBatchStatusStepAction);
        observer.complete();
      }
    }).pipe(mergeMap((next) => of(next)));
  }

  return new Observable((observer) => {
    const onInsuranceTermsModalAccept = shouldShowLegacyCostReview
      ? () =>
          openCostReviewModal({
            fulfillmentPlanId: included[0],
            jobGuid,
            costReviewData,
            observer,
          })
      : () => {
          observer.next(updateLabelBatchStatusStepAction);
          observer.complete();
        };
    const onInsuranceTermsModalClose = () => {
      createLabelSubject.next({
        fulfillmentPlanId: included[0],
        isCreatingLabel: false,
      });
      const lbsModalId = Global.selectBulkLbsModalId(state);
      ModalService.closeModalById(lbsModalId);
      observer.next(BulkActions.ignoreShipsuranceTermsAndConditions(jobGuid));
      observer.complete();
    };

    InsuranceTermsService.launchInsuranceTermsModal(onInsuranceTermsModalAccept, onInsuranceTermsModalClose, insuranceProviderId);
  }).pipe(mergeMap((next) => of(next)));
};

const setCostReviewData = ({
  jobGuid,
  included,
  excluded,
  errored,
  pusherData,
  andPrint,
  shouldShowLegacyCostReview,
  getState,
  appliedSavings,
}) => {
  const state = getState();
  const isSwitchAndSaveRecommendationsEnabled = new FeatureFlags({ state }).enableSwitchAndSaveRateRecommendations;
  const isBulkRateShopperRecommendationsEnabled = getIsBulkRateShopperRecommendationsEnabled(state);
  const setCostReviewDataWithRecommendations = (recommendationSummary) =>
    Actions.openCostReview(
      jobGuid,
      included,
      excluded,
      errored,
      pusherData,
      andPrint,
      shouldShowLegacyCostReview,
      recommendationSummary,
      appliedSavings,
    );

  if (!isSwitchAndSaveRecommendationsEnabled) {
    return of(setCostReviewDataWithRecommendations());
  }

  // We only want to show recommendations on the first pass. If the user applies any recommendations, we don't want to refetch and show them again.
  if (appliedSavings?.value) {
    return of(setCostReviewDataWithRecommendations());
  }

  return from(
    getSummary({ fulfillmentPlanIds: included, isRateShopperRecommendationsEnabled: isBulkRateShopperRecommendationsEnabled }),
  ).pipe(
    mergeMap((response) => of(setCostReviewDataWithRecommendations(response.data))),
    catchError(() => of(setCostReviewDataWithRecommendations())),
  );
};

export const startBulkPurchaseCostReview = ({
  action$,
  included,
  excluded,
  errored,
  andPrint,
  shouldShowLegacyCostReview = false, // This is required for single label
  getState,
  quickshipProps = {},
  shouldAlwaysShowCostReviewDefault,
  pusherTimeoutMs = 10000,
  appliedSavings,
}) => {
  if (included?.length === 0) {
    return of(
      BulkActions.bulkSetActiveLabelBatchStatusStep({
        step: LabelBatchStatusStep.NoOrdersIncluded.ordinal,
      }),
    );
  }

  const costReviewTimeout$ = new Subject();
  const startCostReviewSubject = new BehaviorSubject(false);
  const onFailure = () => {
    costReviewTimeout$.next(BulkActions.bulkSetLabelProcessingEror(COST_REVIEW_TIMEOUT_ERROR));
  };
  return startCostReviewSubject.pipe(
    mergeMap((isRetry) => {
      const logContext = {
        includedFulfillmentPlanIdsLength: Array.isArray(included) ? included.length : included,
        excludedFulfillmentPlanIdsLength: Array.isArray(excluded) ? excluded.length : excluded,
        erroredFulfillmentPlanIdsLength: Array.isArray(errored) ? errored.length : errored,
        purchasePoint: 'bulk',
      };

      const retryOrConcludeFailure = ({ jobGuids, errorOnMessage }) => {
        if (!errorOnMessage) {
          LogService.logInfo(
            !isRetry
              ? 'Timeout waiting for Cost Review pusher message. Automatically retrying...'
              : 'Timeout waiting for Cost Review pusher message (retry). Showing error to user.',
            {
              ...logContext,
              jobGuids,
            },
          );
        }
        if (!isRetry) {
          startCostReviewSubject.next(true);
          return;
        }
        onFailure();
      };

      const hasAcceptedUpsLabelTerms =
        Global.selectSellerSellerSetting(getState(), { settingName: HAS_ACCEPTED_UPS_LABEL_TERMS_SETTING }) === 'true';

      return startCostReviewGetMessages({
        apiCallback: () => BulkApi.costReview(included, hasAcceptedUpsLabelTerms),
        pusherTimeoutMs,
        onError: retryOrConcludeFailure,
        logContext,
      }).pipe(
        mergeMap(({ jobGuids, findMessage$ }) => {
          const [jobGuid] = jobGuids || [];

          const next$ = of(
            BulkActions.bulkSetActiveLabelBatchStatusStep({
              step: LabelBatchStatusStep.CostReview.ordinal,
              jobGuid,
            }),
          );

          return merge(
            isRetry ? costReviewTimeout$ : EMPTY,
            next$,
            findMessage$.pipe(
              takeUntil(costReviewTimeout$),
              take(1),
              mergeMap(({ pusherData }) => {
                return of({
                  pusherData,
                  jobGuid,
                });
              }),
            ),
          );
        }),
        catchError(() => {
          BulkPurchaseNotificationService.showGenericHttpNotification();
          return EMPTY;
        }),
      );
    }),
    takeWhile((next) => !next.pusherData, true),
    switchMap((next) => {
      if (!next.pusherData) {
        return of(next);
      }
      const { jobGuid, pusherData } = next;
      const state = getState();
      const { enableUpsTerms2 } = new FeatureFlags({ state });
      const labelProviders = Global.selectSellerLabelProviders(state);

      // Sync UPS terms setting if backend confirms terms were already accepted
      const cachedHasAcceptedUpsTerms = Global.selectSellerSellerSetting(state, { settingName: HAS_ACCEPTED_UPS_LABEL_TERMS_SETTING });
      const shouldSyncUpsTermsSetting = enableUpsTerms2 && pusherData.upsTermsRequired === false && cachedHasAcceptedUpsTerms !== 'true';

      const actionToUpdateReducer = BulkActions.startProcessingCostReview(
        jobGuid,
        included,
        excluded,
        errored,
        andPrint,
        shouldShowLegacyCostReview,
      );
      const showCostReview = determineShouldShowCostReviewByCostReviewBalances(
        pusherData,
        { ...quickshipProps, isUpsTermsEnabled: enableUpsTerms2, labelProviders },
        shouldAlwaysShowCostReviewDefault,
      );
      const syncUpsTermsAction = shouldSyncUpsTermsSetting
        ? saveNewSellerSettings({ [HAS_ACCEPTED_UPS_LABEL_TERMS_SETTING]: 'true' })
        : null;

      if (!showCostReview) {
        ModalService.closeModalById(costReviewModalId); // this will trigger normal Bulk cancel action. Normal bulk flow looks for the bypass action to continue on. Single label for batch shouldn't care.
        return syncUpsTermsAction
          ? of(actionToUpdateReducer, syncUpsTermsAction, PostagePurchaseActions.bypassCostReview(jobGuid))
          : of(actionToUpdateReducer, PostagePurchaseActions.bypassCostReview(jobGuid));
      }

      return merge(
        syncUpsTermsAction ? of(actionToUpdateReducer, syncUpsTermsAction) : of(actionToUpdateReducer),
        setCostReviewData({
          jobGuid,
          included,
          excluded,
          errored,
          pusherData,
          andPrint,
          shouldShowLegacyCostReview,
          getState,
          appliedSavings,
        }),
        launchCostReviewModal({
          jobGuid,
          included,
          costReviewData: pusherData,
          shouldShowLegacyCostReview,
          getState,
        }),
        onCloseCostReviewModal({ action$ }),
      );
    }),
  );
};

export const waitForEndOfCostReview = (action$) => {
  return action$.pipe(ofType(PostagePurchaseActions.POSTAGE_AND_INSURANCE_PURCHASE_SUCCESS, PostagePurchaseActions.BYPASS_COST_REVIEW));
};

export const applyCostReviewRecommendations = (action$, getState, onSuccess) => {
  return action$.pipe(
    ofType(Actions.COST_REVIEW_APPLY_RECOMMENDATIONS),
    mergeMap(
      ({ jobGuid, bulkUpdateJobGuid, serviceFilters, insuranceFilters, rateShopperFilters, isRateShopperRecommendationsEnabled }) => {
        const request = {
          bulkUpdateJobGuid,
          serviceFilters,
          insuranceFilters,
          rateShopperFilters,
          isRateShopperRecommendationsEnabled,
        };

        const handleSuccess = (amountSaved) => {
          return merge(onSuccess(amountSaved), of(Actions.costReviewApplyRecommendationsSuccess(jobGuid)));
        };

        const handleError = () => {
          return of(Actions.costReviewApplyRecommendationsError(jobGuid));
        };

        return bulkApplyRecommendationsFromLabelPurchaseCostReview(getState, request, handleSuccess, handleError);
      },
    ),
  );
};
